import { Component, EventEmitter, Output, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { SliderModule } from 'primeng/slider';
import { TooltipModule } from 'primeng/tooltip';
import { FormsModule } from '@angular/forms';
import { SliderComponent } from '../slider/slider.component';
import {
  applyAppZoomPercent,
  isAppZoomUserOverride,
  persistAppZoomPercent,
  syncResponsiveAppZoom,
} from '@app/utils/app-zoom.util';

@Component({
  selector: 'app-visual-impairments-panel',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    DialogModule,
    ButtonModule,
    SliderModule,
    TooltipModule,
    FormsModule,
    SliderComponent
  ],
  templateUrl: './visual-impairments-panel.component.html',
  styleUrls: ['./visual-impairments-panel.component.scss']
})
export class VisualImpairmentsPanelComponent implements OnInit, OnDestroy {
  @Output() close = new EventEmitter<void>();
  
  visible = true;
  selectedTheme: 'standard' | 'contrast' | 'blackwhite' = 'standard';
  fontSize = 100; 
  minFontSize = 50;
  maxFontSize = 300;
  fontSizeStep = 10;

  contrastValue = 100;
  minContrast = 100;
  maxContrast = 250;
  contrastStep = 25;

  brightnessValue = 100;
  minBrightness = 50;
  maxBrightness = 150;
  brightnessStep = 10;

  themes: Array<{ id: 'standard' | 'contrast' | 'blackwhite', icon: string, label: string }> = [
    { id: 'standard', icon: 'circle', label: 'Standard' },
    { id: 'contrast', icon: 'adjust', label: 'High Contrast' },
    { id: 'blackwhite', icon: 'adjust', label: 'Black & White' }
  ];

  private resizeTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit() {
    const savedTheme = localStorage.getItem('accessibilityTheme') as 'standard' | 'contrast' | 'blackwhite';
    const savedContrast = localStorage.getItem('contrastValue');
    const savedBrightness = localStorage.getItem('brightnessValue');
    
    if (savedTheme) {
      this.selectedTheme = savedTheme;
      this.applyTheme(savedTheme);
    }

    this.fontSize = syncResponsiveAppZoom();
    
    if (savedContrast) {
      this.contrastValue = parseInt(savedContrast);
      this.applyContrast(this.contrastValue);
    }
    
    if (savedBrightness) {
      this.brightnessValue = parseInt(savedBrightness);
      this.applyBrightness(this.brightnessValue);
    }
  }

  ngOnDestroy(): void {
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
  }

  selectTheme(theme: 'standard' | 'contrast' | 'blackwhite') {
    this.selectedTheme = theme;
    this.applyTheme(theme);
    localStorage.setItem('accessibilityTheme', theme);
  }

  applyTheme(theme: 'standard' | 'contrast' | 'blackwhite') {
    document.body.classList.remove('high-contrast-theme', 'black-and-white-theme');
    switch (theme) {
      case 'contrast':
        document.body.classList.add('high-contrast-theme');
        if (this.contrastValue === 100) {
          this.contrastValue = 150;
          this.applyContrast(this.contrastValue);
        }
        break;
      case 'blackwhite':
        document.body.classList.add('black-and-white-theme');
        break;
      case 'standard':
        this.contrastValue = 100;
        this.applyContrast(100);
        break;
    }
  }

  onFontSizeChange() {
    applyAppZoomPercent(this.fontSize);
    persistAppZoomPercent(this.fontSize, true);
  }

  applyFontSize(size: number) {
    applyAppZoomPercent(size);
  }

  decreaseFontSize() {
    if (this.fontSize > this.minFontSize) {
      this.fontSize -= this.fontSizeStep;
      this.onFontSizeChange();
    }
  }

  increaseFontSize() {
    if (this.fontSize < this.maxFontSize) {
      this.fontSize += this.fontSizeStep;
      this.onFontSizeChange();
    }
  }
  
  onContrastChange(value: number) {
    this.contrastValue = value;
    this.applyContrast(value);
    localStorage.setItem('contrastValue', value.toString());
  }
  
  applyContrast(value: number) {
    const contrastValue = value / 100;
    document.documentElement.style.setProperty('--app-contrast-value', contrastValue.toString());
    if (this.selectedTheme === 'contrast') {
      document.body.style.filter = `contrast(${contrastValue})`;
    } else if (this.selectedTheme === 'blackwhite') {
      document.body.style.filter = `grayscale(1) contrast(${contrastValue})`;
    } else {
      document.body.style.filter = '';
    }
  }
  
  onBrightnessChange(value: number) {
    this.brightnessValue = value;
    this.applyBrightness(value);
    localStorage.setItem('brightnessValue', value.toString());
  }
  
  applyBrightness(value: number) {
    const brightnessValue = value / 100;
    document.documentElement.style.setProperty('--app-brightness-value', brightnessValue.toString());
  }

  closePanel() {
    this.visible = false;
    this.close.emit();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (isAppZoomUserOverride()) {
      return;
    }
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    this.resizeTimer = setTimeout(() => {
      this.fontSize = syncResponsiveAppZoom();
    }, 150);
  }
  
  @HostListener('window:keydown', ['$event'])
  handleKeyboardShortcuts(event: KeyboardEvent) {
    if (event.ctrlKey && !event.shiftKey && !event.altKey) {
      switch (event.key) {
        case '+':
        case '=': 
          event.preventDefault();
          this.increaseFontSize();
          break;
        case '-':
          event.preventDefault();
          this.decreaseFontSize();
          break;
        case '0':
          event.preventDefault();
          this.fontSize = syncResponsiveAppZoom({forceRecommended: true});
          break;
      }
    }
    
    if (event.ctrlKey && event.shiftKey && !event.altKey) {
      switch (event.key) {
        case 'C':
        case 'c':
          event.preventDefault();
          const themes: Array<'standard' | 'contrast' | 'blackwhite'> = ['standard', 'contrast', 'blackwhite'];
          const currentIndex = themes.indexOf(this.selectedTheme);
          const nextIndex = (currentIndex + 1) % themes.length;
          this.selectTheme(themes[nextIndex]);
          break;
        case '+':
        case '=':
          event.preventDefault();
          if (this.selectedTheme !== 'standard' && this.contrastValue < this.maxContrast) {
            this.onContrastChange(Math.min(this.maxContrast, this.contrastValue + this.contrastStep));
          }
          break;
        case '-':
        case '_':
          event.preventDefault();
          if (this.selectedTheme !== 'standard' && this.contrastValue > this.minContrast) {
            this.onContrastChange(Math.max(this.minContrast, this.contrastValue - this.contrastStep));
          }
          break;
      }
    }
    
    if (event.ctrlKey && !event.shiftKey && event.altKey) {
      switch (event.key) {
        case '+':
        case '=':
          event.preventDefault();
          if (this.brightnessValue < this.maxBrightness) {
            this.onBrightnessChange(Math.min(this.maxBrightness, this.brightnessValue + this.brightnessStep));
          }
          break;
        case '-':
          event.preventDefault();
          if (this.brightnessValue > this.minBrightness) {
            this.onBrightnessChange(Math.max(this.minBrightness, this.brightnessValue - this.brightnessStep));
          }
          break;
        case '0':
          event.preventDefault();
          this.brightnessValue = 100;
          this.onBrightnessChange(100);
          break;
      }
    }
  }
}
